import React from 'react';
import { SkuRow } from '../../api/material';
import { HeaderCheckbox, RowCheckbox } from '../../components/BatchActions';
import { SkuPropTag } from '../../components/SkuProp';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { i18nText } from '../../utils/i18n';

interface Props {
    list: SkuRow[];
    loading: boolean;
    sel: ReturnType<typeof useBatchSelect>;
    canDelete: boolean;
    canUpdate: boolean;
    onEdit: (s: SkuRow) => void;
    onDelete: (s: SkuRow) => void;
    /** 点组成里的子 SKU 编码: 跳到单品 tab 查看该 SKU */
    onViewItem: (skuCode: string) => void;
}

const COLS = 7;

// SKU 库「SKU 集合」tab 的表格 (2026-10-19): 集合不绑材料、不填规格, 换成「包含的 SKU」列 (子 SKU × 数量).
function SkuBundleTable({ list, loading, sel, canDelete, canUpdate, onEdit, onDelete, onViewItem }: Props) {
    const canManage = canUpdate || canDelete;
    return (
        <table className="w-full">
            <thead className="bg-slate-50">
                <tr>
                    <th className="th w-8" hidden={!canDelete}>
                        <HeaderCheckbox hidden={!canDelete} sel={sel} />
                    </th>
                    <th className="th">SKU 编码</th>
                    <th className="th">产品名</th>
                    <th className="th w-px whitespace-nowrap">属性</th>
                    <th className="th">包含的 SKU</th>
                    <th className="th">状态</th>
                    <th className="th" hidden={!canManage}>
                        操作
                    </th>
                </tr>
            </thead>
            <tbody>
                {loading && (
                    <tr>
                        <td className="td text-center text-slate-400" colSpan={COLS}>
                            加载中…
                        </td>
                    </tr>
                )}
                {!loading && list.length === 0 && (
                    <tr>
                        <td className="td text-center text-slate-400" colSpan={COLS}>
                            暂无 SKU 集合
                        </td>
                    </tr>
                )}
                {!loading &&
                    list.map((s) => {
                        const items = s.items || [];
                        const total = items.reduce((n, it) => n + it.qty, 0);
                        return (
                            <tr key={s.id} className="hover:bg-slate-50 align-top">
                                <td className="td" hidden={!canDelete}>
                                    <RowCheckbox hidden={!canDelete} sel={sel} k={s.skuCode} />
                                </td>
                                {/* 集合编码常是十几个子编码串起来的, 允许断行, 不把表撑宽 */}
                                <td className="td font-mono text-xs break-all max-w-[18rem]">{s.skuCode}</td>
                                <td className="td min-w-[12rem]">
                                    <div>{i18nText(s.productName || {}) || '—'}</div>
                                    {s.productName?.en && (
                                        <div className="text-[11px] text-slate-400">{s.productName.en}</div>
                                    )}
                                </td>
                                <td className="td whitespace-nowrap">
                                    <SkuPropTag code={s.prop} />
                                </td>
                                <td className="td text-xs min-w-[16rem]">
                                    <div className="text-[11px] text-slate-400 mb-1">
                                        {items.length} 种 / 共 {total} 件
                                    </div>
                                    {items.map((it) => (
                                        <div key={it.skuCode} className="flex items-baseline gap-1.5 leading-6">
                                            {it.missing ? (
                                                <span className="font-mono">{it.skuCode}</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="font-mono text-brand-600 hover:underline"
                                                    title="在单品 SKU 中查看"
                                                    onClick={() => onViewItem(it.skuCode)}
                                                >
                                                    {it.skuCode}
                                                </button>
                                            )}
                                            <span className="font-mono text-brand-700">×{it.qty}</span>
                                            {it.missing ? (
                                                <span className="tag bg-rose-50 text-rose-600">已不在库</span>
                                            ) : (
                                                <span
                                                    className="text-slate-500 inline-block max-w-[12rem] truncate align-bottom"
                                                    title={i18nText(it.productName || {})}
                                                >
                                                    {i18nText(it.productName || {})}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </td>
                                <td className="td whitespace-nowrap">
                                    {s.status === 'on_shelf' ? (
                                        <span className="tag cap-on">在售</span>
                                    ) : (
                                        <span className="tag bg-slate-100 text-slate-500">下架</span>
                                    )}
                                </td>
                                <td className="td whitespace-nowrap text-xs" hidden={!canManage}>
                                    <button
                                        type="button"
                                        className="text-brand-600 hover:underline"
                                        onClick={() => onEdit(s)}
                                        hidden={!canUpdate}
                                    >
                                        编辑
                                    </button>
                                    <button
                                        type="button"
                                        className="ml-2 text-rose-500 hover:underline"
                                        onClick={() => onDelete(s)}
                                        hidden={!canDelete}
                                    >
                                        删除
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
            </tbody>
        </table>
    );
}

export default SkuBundleTable;
